import { useRef, useState } from "react";
import api, { formatApiError } from "@/api/client";
import { toast } from "sonner";
import { FileUp, Sparkles, Loader2 } from "lucide-react";

const CHUNK = 750 * 1024;

export default function ResumeReparse({ card, onChanged, onActivity }) {
  const [busy, setBusy] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const fileRef = useRef(null);

  const run = async (file) => {
    if (!file || busy) return;
    setBusy(true);
    try {
      const { data: s } = await api.post("/resume/parse/start", { filename: file.name, total_size: file.size });
      let index = 0;
      for (let off = 0; off < file.size; off += CHUNK) {
        const fd = new FormData();
        fd.append("index", index);
        fd.append("chunk", file.slice(off, off + CHUNK));
        await api.post(`/uploads/${s.upload_id}/chunk`, fd);
        index += 1;
      }
      const { data: parsed } = await api.post(`/resume/parse/${s.upload_id}/complete`);
      const { data: res } = await api.post(`/cards/${card.id}/reparse-resume`, { token: parsed.token });
      const filled = res.filled || [];
      const skipped = res.skipped || [];
      if (filled.length) {
        toast.success(`Filled ${filled.join(", ")} from the resume · attached`);
      } else if (parsed.error) {
        toast.warning("Couldn't auto-read this resume — attached it anyway");
      } else {
        toast.info(`Nothing missing to fill${skipped.length ? ` — already have ${skipped.join(", ")}` : ""} · resume attached`);
      }
      if (res.card) onChanged?.(res.card);
      onActivity?.();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  return (
    <div
      data-testid="resume-reparse-zone"
      onClick={() => !busy && fileRef.current?.click()}
      onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
      onDragLeave={() => setDragOver(false)}
      onDrop={(e) => { e.preventDefault(); setDragOver(false); run(e.dataTransfer.files?.[0]); }}
      className={`mt-2 rounded-md border border-dashed px-3 py-2 text-center cursor-pointer transition-colors ${
        dragOver ? "border-[#1E73C9] bg-[#1E73C9]/5" : "border-[#15233B]/20 hover:border-[#FBBF24] hover:bg-[#FFF9C4]/30"
      }`}
    >
      <input
        ref={fileRef}
        data-testid="resume-reparse-input"
        type="file"
        accept=".pdf,.png,.jpg,.jpeg,.webp,.docx"
        className="hidden"
        onChange={(e) => run(e.target.files?.[0])}
      />
      {busy ? (
        <div data-testid="resume-reparse-busy" className="flex items-center justify-center gap-2 text-[11px] font-bold text-[#15233B]/70">
          <Loader2 className="h-3.5 w-3.5 animate-spin text-[#1E73C9]" /> AI is reading the resume…
        </div>
      ) : (
        <div className="flex items-center justify-center gap-1.5 text-[11px] font-bold text-[#15233B]/70">
          <FileUp className="h-3.5 w-3.5 text-[#1E73C9]" /> Drop a resume to fill missing details
          <Sparkles className="h-3 w-3 text-[#FBBF24]" />
        </div>
      )}
    </div>
  );
}
